/* eslint-disable no-console */
/* eslint-disable-next-line dot-notation, prettier/prettier */
/* eslint-disable-next-line react/jsx-no-bind */
/* eslint-disable camelcase */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Checkbox,
  Divider,
  HStack,
  VStack,
  Text,
  SimpleGrid,
  RadioGroup,
  Radio,
  Input,
  Select,
  Image,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Spinner,
  background,
} from '@chakra-ui/react';

import { ResponsiveContainer } from 'recharts';
import { useI18n } from '~/i18n';
// import Plot from "react-plotly.js";
import { colors } from '~/styles/colors';
import { toastError, toastSuccess } from '~/helpers/toast';
import { Button } from '~/components/Form/Button/Button_Styles';
import EquipmentService from '~/store/ducks/Equipment/services';
import { CsSelect } from '~/components/CsSelect';
import { CsInput } from '~/components/CsInput';
import { loadCoolProp, propsSI } from '~/helpers/coolPropPro';
import { PHDiagramChart } from '../../components/PHDiagramChart/PHDiagramChart';
import { OperationEnvelopeChart } from '../../components/OperationEnvelopeChart/OperationEnvelopeChart';
import { MatrixWindow, MatrixGenerationValues, MatrixRow } from '~/components/MatrixWindow/MatrixWindow';
import { generatePHDiagramData } from '~/utils/phDiagram';
import type { PHDiagramData } from '~/utils/phDiagram';
import { getOperationEnvelope, isPointInsidePolygon } from '~/utils/operationEnvelope';
import { exportCompressoresExcel, exportCompressoresPdf } from '~/utils/reportExport';
import logo from '~/assets/images/elgin.png';
import { CompressoresContainer } from './Compressores_Styles';
import { getCompressorCalculationModel } from './calculationModels';

type TabKey = 'geral' | 'dados-fisicos';
type SearchMode = 'modelo' | 'capacidade';
type TempUnit = 'c' | 'f';
type Lang = 'pt' | 'us' | 'es';

const ASSET_BASE = '/assets/images/products/compressores/semi-hermetico';

const formatDecimalValue = (value: unknown, decimalPlaces = 2) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(decimalPlaces) : String(value);
};


const Field: React.FC<{ label?: React.ReactNode; suffix?: string; prefix?: string; children: React.ReactNode }> = ({
  label,
  suffix,
  prefix,
  children,
}) => (
  <Box>
    {label ? <Text className="cs__label">{label}</Text> : null}
    <HStack mt={2} spacing={2} align="center">
      {prefix ? <Text className="cs__suffix">{prefix}</Text> : null}
      <Box flex="1">{children}</Box>
      {suffix ? <Text className="cs__suffix">{suffix}</Text> : null}
    </HStack>
  </Box>
);

const Card: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box className="cs__card">
    {title ? <Text className="cs__card-title">{title}</Text> : null}
    {children}
  </Box>
);

type FindModelResult = {
  compressorData: Record<string, any>;
  calculatedCapacity: number;
  fullResults: Record<string, any>;
};

type FindModelsModalProps = {
  isHermetico: boolean;
  isOpen: boolean;
  onClose: () => void;
  requiredCapacityKw: number;
  requiredCapacityDisplay: number;
  capacitySuffix: string;
  results: FindModelResult[];
  applicationFilter: string;
  onApplicationFilterChange: (value: string) => void;
  onSelectModel: (compressorData: Record<string, any>) => void;
  convertCapacityValue: (value: number) => number;
};

const FindModelsModal: React.FC<FindModelsModalProps> = ({
  isHermetico,
  isOpen,
  onClose,
  requiredCapacityKw,
  requiredCapacityDisplay,
  capacitySuffix,
  results,
  applicationFilter,
  onApplicationFilterChange,
  onSelectModel,
  convertCapacityValue,
}) => {
  const { t } = useI18n();

  const applicationOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        results
          .map((item) => String(item.compressorData?.Aplicação || '').trim())
          .filter(Boolean)
      )
    ).sort();

    return ['Todas', ...values];
  }, [results]);

  const filteredResults = useMemo(() => {
    if (applicationFilter === 'Todas') {
      return results;
    }

    return results.filter((item) => String(item.compressorData?.Aplicação || '').trim() === applicationFilter);
  }, [applicationFilter, results]);

  const below = useMemo(() => {
    const matches = filteredResults
      .filter((item) => item.calculatedCapacity < requiredCapacityKw)
      .sort((a, b) => b.calculatedCapacity - a.calculatedCapacity);

    return matches.length ? matches[0] : null;
  }, [filteredResults, requiredCapacityKw]);

  const above = useMemo(() => {
    const matches = filteredResults
      .filter((item) => item.calculatedCapacity >= requiredCapacityKw)
      .sort((a, b) => a.calculatedCapacity - b.calculatedCapacity);

    return matches.length ? matches[0] : null;
  }, [filteredResults, requiredCapacityKw]);

  const renderCard = (title: string, item: FindModelResult | null) => {
    if (!item) {
      return (
        <Card title={title}>
          <Text color="gray.500">{t('Nenhum modelo encontrado.')}</Text>
        </Card>
      );
    }

    const compData = item.compressorData || {};
    const fullResults = item.fullResults || {};

    return (
      <Card title={title}>
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between"><Text fontWeight="700">{t('Modelo')}</Text><Text>{String(compData.Modelo || '-')}</Text></HStack>
          {isHermetico ? (
            <HStack justify="space-between">
              <Text fontWeight="700">{t('Refrigerante')}</Text>
              <Text>{String(compData.Refrigerante || '-')}</Text>
            </HStack>
          ) : null}
          <HStack justify="space-between"><Text fontWeight="700">{t('Aplicação')}</Text><Text>{String(compData['Aplicação'] || '-')}</Text></HStack>
          <HStack justify="space-between"><Text fontWeight="700">{t('Capacidade')} ({capacitySuffix})</Text><Text>{Number.isFinite(item.calculatedCapacity) ? convertCapacityValue(item.calculatedCapacity).toFixed(2) : '-'}</Text></HStack>
          <HStack justify="space-between">
            <Text fontWeight="700">{t('Potência (kW)')}</Text>
            <Text>
              {Number.isFinite(fullResults.potencia_consumida ?? fullResults.potenciaConsumida)
                ? Number(fullResults.potencia_consumida ?? fullResults.potenciaConsumida).toFixed(2)
                : '-'}
            </Text>
          </HStack>
          <HStack justify="space-between"><Text fontWeight="700">{t('COP')}</Text><Text>{Number.isFinite(fullResults.cop) ? fullResults.cop.toFixed(2) : '-'}</Text></HStack>
          <HStack justify="space-between"><Text fontWeight="700">{t('Corrente (A)')}</Text><Text>{Number.isFinite(fullResults.corrente) ? fullResults.corrente.toFixed(2) : '-'}</Text></HStack>
          <Button
            $primary
            style={{ width: '100%', fontSize: '16px', background: colors.primary, color: colors.white }}
            onClick={() => onSelectModel(compData)}
          >
            {t('Selecionar este Modelo')}
          </Button>
        </VStack>
      </Card>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('Seleção de Compressor por Capacidade')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between" align="center" flexWrap="wrap">
              <Text fontWeight="700">{t('Capacidade Necessária')}: {Number.isFinite(requiredCapacityDisplay) ? requiredCapacityDisplay.toFixed(2) : '0.00'} {capacitySuffix}</Text>
              <HStack>
                <Text>{t('Filtrar por aplicação')}</Text>
                <Select value={applicationFilter} onChange={(e) => onApplicationFilterChange(e.target.value)} maxW="240px">
                  {applicationOptions.map((option) => (
                    <option key={option} value={option}>{option === 'Todas' ? t('Todas') : option}</option>
                  ))}
                </Select>
              </HStack>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {renderCard(t('Modelo abaixo mais próximo'), below)}
              {renderCard(t('Modelo acima mais próximo'), above)}
            </SimpleGrid>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>{t('Fechar')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const Compressores: React.FC = () => {
  const { t } = useI18n();
  const location = useLocation();
  const isGuestAccess = location.pathname.startsWith('/guest/compressores/');
  const isHermetico = location.pathname.includes('/compressores-hermetico/') || location.pathname.endsWith('/hermetico');
  const calculationModel = getCompressorCalculationModel(isHermetico ? 'hermetico' : 'semi-hermetico');
  // Top-left (Parâmetros)
  const [loading, setLoading] = useState(false);

  const [compressorParams, setCompressorParams] = useState({
    unit: isHermetico ? 'kcalh' : 'kw',
    frequencia: '',
    tensao: '',
    refrigerante: '',
    modelo: '',
    tempUnit: 'c',
    lang: '',
    capacidade: '',
    temperaturaCondensacao: '',
    temperaturaEvaporacao: '',
    superaquecimentoTotal: '',
    superaquecimentoUtil: '',
    useSuperaquecimentoUtil: false,
    subResfriamento: ''
  });

  const {
    unit,
    frequencia,
    tensao,
    refrigerante,
    modelo,
    tempUnit,
    capacidade,
    temperaturaCondensacao,
    temperaturaEvaporacao,
    superaquecimentoTotal,
    superaquecimentoUtil,
    useSuperaquecimentoUtil,
    subResfriamento
  } = compressorParams;

  const updateCompressorParam = (field: string, value: any) => {
    setCompressorParams((prev) => {
      const nextParams = {
        ...prev,
        [field]: value
      };

      if (field === 'modelo') {
        nextParams.frequencia = '';
        nextParams.tensao = '';
        nextParams.refrigerante = '';
      }

      if (field === 'frequencia') {
        nextParams.tensao = '';
        nextParams.refrigerante = '';
      }

      if (field === 'tensao') {
        nextParams.refrigerante = '';
      }

      return nextParams;
    });
  };

  const [compressors, setCompressors] = useState([]);
  const [compressorData, setCompressorData] = useState<Record<string, string>>({});
  const compressorHousing = String(compressorData?.['Carcaça'] || '').trim();
  const compressorImageBase =
    isHermetico && modelo && compressorHousing ? `${ASSET_BASE}/Carcaca_${compressorHousing}` : '';
  const [compressorImageExtension, setCompressorImageExtension] = useState<'png' | 'jpg'>('png');
  let compressorImage = `${ASSET_BASE}/Imagem_6.png`;

  if (isHermetico) {
    compressorImage = compressorImageBase ? `${compressorImageBase}.${compressorImageExtension}` : '';
  }

  useEffect(() => {
    setCompressorImageExtension('png');
  }, [compressorImageBase]);

  const [compressorResult, setCompressorResult] = useState({
    capacidadeRefrigeracao: 0,
    capacidadeRefrigeracaoNormativa: 0,
    calorRejeitado: 0,
    cop: 0,
    copNormativo: 0,
    potenciaConsumida: 0,
    corrente: 0,
    refrigerante: '',
    vazaoMassica: 0,
    deslocamentoNominal: 0,
    pressaoDescarga: 0,
    pressaoSuccao: 0,
    temperaturaDescarga: 0,
  });
  const [phDiagramData, setPhDiagramData] = useState<PHDiagramData | null>(null);
  const [phDiagramLoading, setPhDiagramLoading] = useState(false);
  const [isMatrixOpen, setIsMatrixOpen] = useState(false);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixRows, setMatrixRows] = useState<MatrixRow[]>([]);
  const [isFindModelsOpen, setIsFindModelsOpen] = useState(false);
  const [findModelsLoading, setFindModelsLoading] = useState(false);
  const [findModelsResults, setFindModelsResults] = useState<FindModelResult[]>([]);
  const [findModelsRequiredCapacity, setFindModelsRequiredCapacity] = useState(0);
  const [findModelsApplicationFilter, setFindModelsApplicationFilter] = useState('Todas');
  const pageRef = useRef<HTMLDivElement | null>(null);

  const getCompressors = async (data) => {
    setLoading(true);
    try {
      const response = isHermetico
        ? await EquipmentService.getCompressorHermetico(data)
        : await EquipmentService.getCompressor(data);
      setCompressors(response);
      setLoading(false);
      console.log(response);
    } catch (err) {
      toastError('project', t('Erro ao recuperar compressores.'));
      console.error(err);
    }
  };

  const capacitySuffix = useMemo(() => {
    if (compressorParams.unit === 'kcalh') return 'kcal/h';
    if (compressorParams.unit === 'kw') return 'kW';
    return 'BTU/h';
  }, [compressorParams.unit]);

  const temperatureSuffix = tempUnit === 'f' ? 'ºF' : 'ºC';

  const convertCapacity = (value: number) => {
    if (!Number.isFinite(value)) return 0;
    if (compressorParams.unit === 'kcalh') return value * 859.8452278589858;
    if (compressorParams.unit === 'btuh') return value * 3412.14163312794;
    return value;
  };

  const capacityToKw = (value: number, unitValue = compressorParams.unit) => {
    if (!Number.isFinite(value)) return 0;
    if (unitValue === 'kcalh') return value / 859.8452278589858;
    if (unitValue === 'btuh') return value / 3412.14163312794;
    return value;
  };

  const displayTemperature = (valueC: number) => {
    if (!Number.isFinite(valueC)) return 0;
    return tempUnit === 'f' ? (valueC * 9) / 5 + 32 : valueC;
  };

  const parseInputNumber = (value: string | number | undefined) => {
    const normalized = String(value ?? '').replace(',', '.').trim();
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const deltaFToDeltaC = (value: number) => value / 1.8;
  const fahrenheitToCelsius = (value: number) => ((value - 32) * 5) / 9;
  const celsiusToFahrenheit = (value: number) => ((value * 9) / 5) + 32;

  const normalizeTemperatureToCelsius = (value: string | number | undefined, unitValue = tempUnit) => {
    const parsed = parseInputNumber(value);
    return unitValue === 'f' ? fahrenheitToCelsius(parsed) : parsed;
  };

  const convertDisplayedTemperatureValue = (
    value: string | number | undefined,
    fromUnit: TempUnit,
    toUnit: TempUnit,
  ) => {
    const parsed = parseInputNumber(value);

    if (!Number.isFinite(parsed)) {
      return '';
    }

    if (fromUnit === toUnit) {
      return String(parsed);
    }

    const valueInCelsius = fromUnit === 'f' ? fahrenheitToCelsius(parsed) : parsed;
    const converted = toUnit === 'f' ? celsiusToFahrenheit(valueInCelsius) : valueInCelsius;
    return converted.toFixed(1);
  };

  const convertDisplayedCapacityValue = (
    value: string | number | undefined,
    fromUnit: string,
    toUnit: string,
  ) => {
    const parsed = parseInputNumber(value);

    if (!Number.isFinite(parsed)) {
      return '';
    }

    const valueInKw = capacityToKw(parsed, fromUnit);

    if (toUnit === 'kcalh') {
      return (valueInKw * 859.8452278589858).toFixed(2);
    }

    if (toUnit === 'btuh') {
      return (valueInKw * 3412.14163312794).toFixed(2);
    }

    return valueInKw.toFixed(2);
  };

  const normalizeParamsToBaseUnits = (params) => ({
    ...params,
    temperaturaCondensacao: String(normalizeTemperatureToCelsius(params.temperaturaCondensacao, params.tempUnit || tempUnit)),
    temperaturaEvaporacao: String(normalizeTemperatureToCelsius(params.temperaturaEvaporacao, params.tempUnit || tempUnit)),
  });

  const isPointInsideEnvelope = (
    selectedRefrigerant: string,
    evapTempC: number,
    condTempC: number,
    application?: string
  ) => {
    const envelope = getOperationEnvelope(
      calculationModel.family,
      selectedRefrigerant,
      application
    );

    if (!envelope.polygons.length) {
      return true;
    }

    return envelope.polygons.some((polygon) => isPointInsidePolygon(evapTempC, condTempC, polygon));
  };

  const buildMatrixInitialValues = (): MatrixGenerationValues => ({
    evapMin: tempUnit === 'f' ? (((-45 * 9) / 5) + 32).toFixed(1) : '-45.0',
    evapStep: tempUnit === 'f' ? '9.0' : '5.0',
    evapMax: tempUnit === 'f' ? (((15 * 9) / 5) + 32).toFixed(1) : '15.0',
    condMin: tempUnit === 'f' ? (((20 * 9) / 5) + 32).toFixed(1) : '20.0',
    condStep: tempUnit === 'f' ? '9.0' : '5.0',
    condMax: tempUnit === 'f' ? (((60 * 9) / 5) + 32).toFixed(1) : '60.0',
    subcooling: String(subResfriamento || '0'),
    superheat: String(superaquecimentoTotal || '0'),
  });

  const uniqueOptions = (values: any[]) => Array.from(new Set(values.filter(Boolean)));

  const filteredByModelo = useMemo(
    () => (modelo ? compressors.filter((item) => item.Modelo === modelo) : compressors),
    [compressors, modelo]
  );

  const filteredByFrequencia = useMemo(
    () => (frequencia ? filteredByModelo.filter((item) => String(item.Frequencia) === String(frequencia)) : filteredByModelo),
    [filteredByModelo, frequencia]
  );

  const filteredByTensao = useMemo(
    () => (tensao ? filteredByFrequencia.filter((item) => String(item['Tensão (V)']) === String(tensao)) : filteredByFrequencia),
    [filteredByFrequencia, tensao]
  );

  const modeloOptions = useMemo(
    () => uniqueOptions(compressors.map((item) => item.Modelo)).map((value) => ({ value: String(value), label: String(value) })),
    [compressors]
  );

  const frequenciaOptions = useMemo(
    () => uniqueOptions(filteredByModelo.map((item) => item.Frequencia)).map((value) => ({ value: String(value), label: `${value} Hz` })),
    [filteredByModelo]
  );

  const tensaoOptions = useMemo(
    () => uniqueOptions(filteredByFrequencia.map((item) => item['Tensão (V)'])).map((value) => ({ value: String(value), label: `${value}V` })),
    [filteredByFrequencia]
  );

  const refrigeranteOptions = useMemo(
    () =>
      uniqueOptions(
        filteredByTensao.flatMap((item) =>
          String(item.Refrigerante || '')
            .split('/')
            .map((value) => value.trim())
            .filter(Boolean)
        )
      ).map((value) => ({ value: String(value), label: String(value) })),
    [filteredByTensao]
  );

  const capacidadeOptions = useMemo(
    () =>
      [
        { value: 'kw', label: 'kW' },
        { value: 'kcalh', label: 'kcal/h' },
        { value: 'btuh', label: 'Btu/h' },
      ],
    [filteredByTensao]
  );

  const temperaturaOptions = useMemo(
    () =>
      [
        { value: 'c', label: 'ºC' },
        { value: 'f', label: 'ºF' },
      ],
    [filteredByTensao]
  );

  const [searchMode, setSearchMode] = useState<SearchMode>('modelo');
  const [tab, setTab] = useState<TabKey>('geral');

  const buildInputRows = () => [
    { label: t('Modo de busca'), value: searchMode === 'capacidade' ? t('Por capacidade') : t('Por modelo') },
    { label: t('Modelo'), value: modelo || '-' },
    { label: t('Frequência'), value: frequencia ? `${frequencia} Hz` : '-' },
    { label: t('Tensão'), value: tensao ? `${tensao} V` : '-' },
    { label: t('Refrigerante'), value: refrigerante || '-' },
    { label: t('Temperatura de condensação'), value: temperaturaCondensacao ? `${temperaturaCondensacao} ${temperatureSuffix}` : '-' },
    { label: t('Temperatura de evaporação'), value: temperaturaEvaporacao ? `${temperaturaEvaporacao} ${temperatureSuffix}` : '-' },
    { label: t('Sub-resfriamento'), value: subResfriamento || '-' },
    { label: t('Superaquecimento total'), value: superaquecimentoTotal || '-' },
    { label: t('Superaquecimento útil'), value: useSuperaquecimentoUtil ? superaquecimentoUtil || '-' : t('Desabilitado') },
    { label: t('Unidade de capacidade'), value: capacitySuffix },
  ];

  const buildResultRows = () => [
    { label: `${t('Capacidade de Refrigeração')} (${capacitySuffix})`, value: convertCapacity(compressorResult.capacidadeRefrigeracao).toFixed(2) },
    {
      label: `${t('Capacidade')} ${calculationModel.standardDisplayName(compressorData?.['Aplicação'])} (${capacitySuffix})`,
      value: convertCapacity(compressorResult.capacidadeRefrigeracaoNormativa).toFixed(2),
    },
    { label: `${t('Calor Rejeitado')} (${capacitySuffix})`, value: convertCapacity(compressorResult.calorRejeitado).toFixed(2) },
    { label: t('COP'), value: compressorResult.cop.toFixed(2) },
    {
      label: t(`COP ${calculationModel.standardDisplayName(compressorData?.['Aplicação'])}`),
      value: compressorResult.copNormativo.toFixed(2),
    },
    { label: `${t('Potência Consumida')} (kW)`, value: compressorResult.potenciaConsumida.toFixed(2) },
    { label: t('Corrente (A)'), value: compressorResult.corrente.toFixed(2) },
    { label: `${t('Temperatura de Descarga')} (${temperatureSuffix})`, value: displayTemperature(compressorResult.temperaturaDescarga).toFixed(1) },
    { label: `${t('Pressão de Sucção')} (bar)`, value: compressorResult.pressaoSuccao.toFixed(2) },
    { label: `${t('Pressão de Descarga')} (bar)`, value: compressorResult.pressaoDescarga.toFixed(2) },
    { label: `${t('Vazão Mássica')} (kg/h)`, value: compressorResult.vazaoMassica.toFixed(2) },
    { label: `${t('Deslocamento Nominal')} (m3/h)`, value: compressorResult.deslocamentoNominal.toFixed(2) },
  ];

  const handleSearchModeChange = (v: string) => {
    setSearchMode(v === 'capacidade' ? 'capacidade' : 'modelo');
  };

  const PA_TO_BAR = 100000;
  const J_KG_TO_KJ_KG = 1000;
  const M3_H_TO_M3_S = 3600;
  const KW_TO_KCALH = 859.845;
  const KW_TO_BTUH = 3412.14;

  function calcularRendimentoVolumetrico(
    P1, P2, tSucaoK, tDescargaK, c, nExp
  ): number {

    if (P1 <= 0 || P2 <= P1 || tSucaoK <= 0 || tDescargaK <= tSucaoK)
      return 0.0

    try {
      const ratioPressureVol = P2 / P1;
      const termoFolgaPressao = 1 + c - c * (ratioPressureVol ** (1 / nExp));
      const nCalibracao = calculationModel.volumetricCalibrationExponent(ratioPressureVol);
      const termoAquecimento = (tSucaoK / tDescargaK) ** nCalibracao;
      const rendimentoFinal = termoFolgaPressao * termoAquecimento;
      return Math.max(0.0, Math.min(1.0, rendimentoFinal));
    } catch (err) {
      toastError('project', `${t('Erro no cálculo do rendimento volumétrico')}: ${err instanceof Error ? err.message : err}`);
      return 0;
    }
  }

  async function calcularCapacidadeSuperaquecimentoEspecifico(
    superheat_k, evap_temp_c, P1, P2, entalpiaH4, rendimento_isentropico, compressor_data, l_refrigerante
  ) {
    const freq = compressor_data.Frequencia;
    const col_deslocamento = `Deslocamento ${parseInt(freq, 10)}Hz (m3h)`;
    const deslocamento_volumetrico_m3h = parseFloat(compressor_data[col_deslocamento] || '0');
    const c_vol = parseFloat(compressor_data['C-Volumetrico'] || '0');
    const n_vol = parseFloat(compressor_data['N-Volumetrico'] || '0');

    const T1_cap_K = evap_temp_c + superheat_k + 273.15;
    const H1_cap =
      (await propsSI('H', 'P', P1, 'T', T1_cap_K, l_refrigerante)) / J_KG_TO_KJ_KG;
    const S1_cap = await propsSI('S', 'P', P1, 'T', T1_cap_K, l_refrigerante);
    const rho1_cap = await propsSI('D', 'P', P1, 'T', T1_cap_K, l_refrigerante);

    const H2_ideal_cap =
      (await propsSI('H', 'P', P2, 'S', S1_cap, l_refrigerante)) / J_KG_TO_KJ_KG;
    const H2_real_cap =
      rendimento_isentropico > 0
        ? H1_cap + (H2_ideal_cap - H1_cap) / rendimento_isentropico
        : H1_cap;
    const T2_real_cap_K = await propsSI(
      'T',
      'H',
      H2_real_cap * J_KG_TO_KJ_KG,
      'P',
      P2,
      l_refrigerante,
    );

    const rend_vol_cap = calcularRendimentoVolumetrico(
      P1, P2, T1_cap_K, T2_real_cap_K, c_vol, n_vol
    );

    const deslocamento_com_rend_cap_m3h = deslocamento_volumetrico_m3h * rend_vol_cap;
    const displacementWithEfficiencyM3s = deslocamento_com_rend_cap_m3h / M3_H_TO_M3_S;
    const massFlowCapKgs = displacementWithEfficiencyM3s * rho1_cap;

    return massFlowCapKgs * (H1_cap - entalpiaH4);
  }

  function calcularRendimentoIsentropico(pressure1, pressure2, etaRef, coefficientB): number {
    if (pressure1 <= 0 || pressure2 <= pressure1) {
      return 0;
    }

    try {
      const pressureRatio = calculationModel.isentropicPressureRatio(pressure1, pressure2);
      const term = pressureRatio ** coefficientB;
      const isentropicEfficiency = etaRef * term;
      return Math.max(0, Math.min(1, isentropicEfficiency));
    } catch (err) {
      toastError('project', `Erro no cálculo do rendimento isentrópico: ${err instanceof Error ? err.message : err}`);
      return 0;
    }
  }

  async function calculate(data, params) {
    try {
      const normalizedParams = normalizeParamsToBaseUnits(params);
      const l_refrigerante = normalizedParams.refrigerante;
      const condensing_temp_c = parseFloat(normalizedParams.temperaturaCondensacao || '0');
      const evap_temp_c = parseFloat(normalizedParams.temperaturaEvaporacao || '0');
      const subcooling_k = parseFloat(normalizedParams.subResfriamento || '0');
      const total_superheat_k = parseFloat(normalizedParams.superaquecimentoTotal || '0');
      const use_useful_superheat = normalizedParams.useSuperaquecimentoUtil;
      const useful_superheat_k = use_useful_superheat ? parseFloat(normalizedParams.superaquecimentoUtil || '0') : 0;

      const freq = data.Frequencia || '0';
      const col_deslocamento = `Deslocamento ${parseInt(freq, 10)}Hz (m3h)`;
      const deslocamento_volumetrico_m3h = parseFloat(data[col_deslocamento] || '0');
      
      const tensao_v = parseFloat(data['Tensão (V)'] || '0');
      
      const eta_ref = parseFloat(data['C-Isoentropico'] || '0');
      const B = parseFloat(data['B-Isoentropico'] || '0');
      const c_vol = parseFloat(data['C-Volumetrico'] || '0');
      const n_vol = parseFloat(data['N-Volumetrico'] || '0');

      if (use_useful_superheat && useful_superheat_k > total_superheat_k) {
        toastError('project', 'O superaquecimento útil não pode ser maior que o superaquecimento total.');
        return null;
      }

      const T_evap_K = evap_temp_c + 273.15;
      const T1_K = evap_temp_c + total_superheat_k + 273.15;

      const P1 = await propsSI('P', 'T', T_evap_K, 'Q', 1, l_refrigerante);
      const T_sat_P1 = await propsSI('T', 'P', P1, 'Q', 1, l_refrigerante);

      if (T1_K <= T_sat_P1) {
        toastError('project', 'A temperatura de sucção (T1) deve ser maior que a temperatura de saturação.');
        return null;
      }

      const entalpiaH1 = (await propsSI('H', 'P', P1, 'T', T1_K, l_refrigerante)) / J_KG_TO_KJ_KG;
      const entropiaS1 = await propsSI('S', 'P', P1, 'T', T1_K, l_refrigerante);
      const densidade_rho1 = await propsSI('D', 'P', P1, 'T', T1_K, l_refrigerante);

      const P2 = await propsSI('P', 'T', condensing_temp_c + 273.15, 'Q', 0, l_refrigerante);

      const rendimento_isentropico = calcularRendimentoIsentropico(P1, P2, eta_ref, B);
      const entalpiaH2_ideal = (await propsSI('H', 'P', P2, 'S', entropiaS1, l_refrigerante)) / J_KG_TO_KJ_KG;
      const entalpiaH2_real =
        rendimento_isentropico > 0
          ? entalpiaH1 + (entalpiaH2_ideal - entalpiaH1) / rendimento_isentropico
          : entalpiaH1;

      const temperaturaT2_real_K = await propsSI('T', 'H', entalpiaH2_real * J_KG_TO_KJ_KG, 'P', P2, l_refrigerante);

      const rendimento_volumetrico_final = calcularRendimentoVolumetrico(
        P1,
        P2,
        T1_K,
        temperaturaT2_real_K,
        c_vol,
        n_vol,
      );

      const P3 = P2;
      let entalpiaH3 = 0;
      let T3_K = 0;

      if (subcooling_k < 1e-6) {
        entalpiaH3 = (await propsSI('H', 'P', P3, 'Q', 0, l_refrigerante)) / J_KG_TO_KJ_KG;
        T3_K = await propsSI('T', 'P', P3, 'Q', 0, l_refrigerante);
      } else {
        T3_K = condensing_temp_c - subcooling_k + 273.15;
        const T_sat_P3 = await propsSI('T', 'P', P3, 'Q', 0, l_refrigerante);
        if (T3_K >= T_sat_P3) {
          T3_K = T_sat_P3 - 0.1;
        }
        entalpiaH3 = (await propsSI('H', 'P', P3, 'T', T3_K, l_refrigerante)) / J_KG_TO_KJ_KG;
      }

      const entalpiaH4 = entalpiaH3;
      const P4 = P1;
      const temperaturaT4 = await propsSI('T', 'H', entalpiaH4 * J_KG_TO_KJ_KG, 'P', P4, l_refrigerante);

      const deslocamento_volumetrico_com_rendimento_m3h = deslocamento_volumetrico_m3h * rendimento_volumetrico_final;
      const deslocamento_volumetrico_m3s = deslocamento_volumetrico_com_rendimento_m3h / M3_H_TO_M3_S;
      const vazao_massica_kgs = deslocamento_volumetrico_m3s * densidade_rho1;
      const vazao_massica_kgh = vazao_massica_kgs * M3_H_TO_M3_S;

      const potencia_consumida = vazao_massica_kgs * (entalpiaH2_real - entalpiaH1);
      const calor_rejeitado = vazao_massica_kgs * (entalpiaH2_real - entalpiaH3);

      let capCprs = 0;
      if (use_useful_superheat) {
        capCprs = await calcularCapacidadeSuperaquecimentoEspecifico(
          useful_superheat_k,
          evap_temp_c,
          P1,
          P2,
          entalpiaH4,
          rendimento_isentropico,
          data,
          l_refrigerante,
        );
      } else {
        capCprs = vazao_massica_kgs * (entalpiaH1 - entalpiaH4);
      }

      const cop = potencia_consumida > 0 ? capCprs / potencia_consumida : 0;
      const potencia_eixo_kw = potencia_consumida;
      const k_p = parseFloat(data.Elet_Kp || '0') / 100;
      const q_const = parseFloat(data.Elet_Qc || '0') * 10;
      const potencia_ativa_eletrica_kw = potencia_eixo_kw + (k_p * potencia_eixo_kw ** 2);
      const potencia_aparente_kva = Math.sqrt(potencia_ativa_eletrica_kw ** 2 + q_const ** 2);
      const denominador = calculationModel.currentDenominator(tensao_v, data.Fase);
      const corrente_A = denominador > 0 ? (potencia_aparente_kva * 1000) / denominador : 0;

      return {
        'refrigerante': l_refrigerante,
        'deslocamento_nominal': deslocamento_volumetrico_m3h,
        'deslocamento_com_rendimento': deslocamento_volumetrico_com_rendimento_m3h,
        'vazao_massica': vazao_massica_kgh,
        'entalpia_h1': entalpiaH1, 'entalpia_h2_real': entalpiaH2_real, 'entalpia_h3': entalpiaH3, 'entalpia_h4': entalpiaH4,
        'pressao_descarga': P2 / PA_TO_BAR, 'pressao_succao': P1 / PA_TO_BAR,
        'temp_descarga_ideal': await propsSI('T', 'P', P2, 'S', entropiaS1, refrigerante) - 273.15,
        'temp_descarga_real': temperaturaT2_real_K - 273.15,
        'capacidade_refrigeracao': capCprs,
        'potencia_consumida': potencia_eixo_kw,
        'calor_rejeitado': calor_rejeitado,
        'cop': cop,
        'corrente': corrente_A,
        'P1': P1, 'P2': P2, 'P3': P3, 'P4': P4,
        'T1': T1_K, 'T2': temperaturaT2_real_K, 'T3': T3_K, 'T4': temperaturaT4,
        'rendimentos_utilizados': {
            'volumetrico': rendimento_volumetrico_final,
            'isentropico': rendimento_isentropico,
            'eta_ref': eta_ref,
            'B': B
        },
        'densidade': densidade_rho1,
        'temp_evaporacao': evap_temp_c,
        'temp_condensacao': condensing_temp_c
      };
    } catch (err) {
      toastError('project', 'Falha no cálculo');
      console.error(err);
      return null;
    }
  }


  const resultadosToDiagramInput = (result) => ({
    refrigerante: result.refrigerante,
    entalpia_h1: result.entalpia_h1,
    entalpia_h2_real: result.entalpia_h2_real,
    entalpia_h3: result.entalpia_h3,
    entalpia_h4: result.entalpia_h4,
    P1: result.P1,
    P2: result.P2,
    P3: result.P3,
    P4: result.P4,
  });

  async function calculateStandard(data, params) {
    console.log('params => ', params);

    const paramsNorma = { ...params };  // Faz uma cópia do params
    const evapTempC = normalizeTemperatureToCelsius(paramsNorma['temperaturaEvaporacao'], paramsNorma.tempUnit || tempUnit);
    const condTempC = normalizeTemperatureToCelsius(paramsNorma['temperaturaCondensacao'], paramsNorma.tempUnit || tempUnit);
    const standardConditions = calculationModel.standardConditions(evapTempC, data['Aplicação']);

    paramsNorma['tempUnit'] = 'c';
    paramsNorma['temperaturaEvaporacao'] = String(evapTempC);
    paramsNorma['temperaturaCondensacao'] = String(
      standardConditions.condensingTemperatureC === undefined
        ? condTempC
        : standardConditions.condensingTemperatureC
    );
    paramsNorma['subResfriamento'] = String(standardConditions.subcoolingK);
    paramsNorma['superaquecimentoTotal'] = String(standardConditions.suctionTemperatureC - evapTempC);
    paramsNorma['useSuperaquecimentoUtil'] = false;

    console.log('paramsNorma => ', paramsNorma);

    const standardResult = await calculate(data, paramsNorma);

    console.log(`${calculationModel.standardName} result => `, standardResult);

    if (!standardResult) {
      return null;
    }

    return {
      capacidadeNormativa: standardResult.capacidade_refrigeracao,
      potenciaConsumidaNormativa: standardResult.potencia_consumida,
      copNormativo: standardResult.cop,
      tempDescargaRealNormativa: standardResult.temp_descarga_real,
      calorRejeitadoNormativo: standardResult.calor_rejeitado,
      correnteNormativa: standardResult.corrente,
    };
  }

  const handleCalculate = async (data, params) => {
    const selectedData = compressors.find((item) => {
      const itemRefrigerants = String(item.Refrigerante || '')
        .split('/')
        .map((value) => value.trim());

      return (
        item.Modelo === params.modelo &&
        String(item.Frequencia) === String(params.frequencia) &&
        String(item['Tensão (V)']) === String(params.tensao) &&
        itemRefrigerants.includes(params.refrigerante)
      );
    });

    if (!selectedData) {
      toastError(
        'project',
        `A combinação de modelo, frequência, tensão e refrigerante não existe na base ${
          isHermetico ? 'Hermético' : 'Semi-Hermético'
        }.`
      );
      return;
    }

    const normalizedParams = normalizeParamsToBaseUnits(params);
    const evaporationTemperatureC = parseFloat(normalizedParams.temperaturaEvaporacao || '0');
    const condensingTemperatureC = parseFloat(normalizedParams.temperaturaCondensacao || '0');
    if (
      !isPointInsideEnvelope(
        params.refrigerante,
        evaporationTemperatureC,
        condensingTemperatureC,
        selectedData['Aplicação']
      )
    ) {
      toastError('project', 'O ponto de operação está fora do envelope permitido para este compressor.');
      return;
    }

    console.log(
      `[Compressores] Cálculo selecionado: ${isHermetico ? 'Hermético' : 'Semi-Hermético'} | Fonte: ${
        isHermetico ? 'EquipmentService.getCompressorHermetico' : 'EquipmentService.getCompressor'
      } | Modelo: ${selectedData.Modelo} | Frequência: ${selectedData.Frequencia} Hz | Tensão: ${
        selectedData['Tensão (V)']
      } V | Refrigerante: ${params.refrigerante}`
    );
    setPhDiagramLoading(true);

    try {
      const result = await calculate(selectedData, params);
      if (!result) {
        setPhDiagramData(null);
        return;
      }

      console.log('result => ', result);

      const standardResult = await calculateStandard(selectedData, params);
      if (!standardResult) {
        setPhDiagramData(null);
        return;
      }

      setCompressorResult({
        capacidadeRefrigeracao: result.capacidade_refrigeracao,
        capacidadeRefrigeracaoNormativa: standardResult.capacidadeNormativa,
        calorRejeitado: result.calor_rejeitado,
        cop: result.cop,
        copNormativo: standardResult.copNormativo,
        potenciaConsumida: result.potencia_consumida,
        corrente: result.corrente,
        refrigerante: result.refrigerante,
        vazaoMassica: result.vazao_massica,
        deslocamentoNominal: result.deslocamento_nominal,
        pressaoDescarga: result.pressao_descarga,
        pressaoSuccao: result.pressao_succao,
        temperaturaDescarga: result.temp_descarga_real,
      });

      const nextDiagramData = await generatePHDiagramData(resultadosToDiagramInput(result), tempUnit === 'f' ? '°F' : '°C');
      setPhDiagramData(nextDiagramData);
    } catch (err) {
      console.error(err);
      toastError('project', 'Falha ao gerar o diagrama P-h.');
      setPhDiagramData(null);
    } finally {
      setPhDiagramLoading(false);
    }
  };

  const handleSelectFoundModel = async (selectedModelData: Record<string, any>) => {
    const nextParams = {
      ...compressorParams,
      modelo: String(selectedModelData.Modelo || ''),
      frequencia: String(selectedModelData.Frequencia || ''),
      tensao: String(selectedModelData['Tensão (V)'] || ''),
      refrigerante: refrigerante || String(selectedModelData.Refrigerante || '').split('/').map((value) => value.trim()).filter(Boolean)[0] || '',
    };

    setCompressorParams(nextParams);
    setCompressorData(selectedModelData);
    setSearchMode('modelo');
    setIsFindModelsOpen(false);

    await handleCalculate(selectedModelData, nextParams);
  };

  const handleFindModels = async () => {
    try {
      const requiredCapacityKw = capacityToKw(parseInputNumber(capacidade));
      const selectedFreq = parseInputNumber(frequencia);
      const selectedVolt = parseInputNumber(tensao);

      if (!requiredCapacityKw) {
        throw new Error('Informe a capacidade necessária para buscar os modelos.');
      }

      if (!selectedFreq || !selectedVolt) {
        throw new Error('Selecione frequência e tensão antes de buscar os modelos.');
      }

      if (isHermetico && !refrigerante) {
        throw new Error('Selecione um refrigerante antes de buscar por capacidade.');
      }

      setFindModelsLoading(true);
      setFindModelsResults([]);
      setFindModelsApplicationFilter('Todas');
      setFindModelsRequiredCapacity(requiredCapacityKw);

      const filtered = compressors.filter((item) => {
        const matchesFrequencyAndVoltage =
          Number.parseFloat(String(item.Frequencia || 0)) === selectedFreq
          && Number.parseFloat(String(item['Tensão (V)'] || 0)) === selectedVolt;

        if (!matchesFrequencyAndVoltage || !isHermetico) {
          return matchesFrequencyAndVoltage;
        }

        const itemRefrigerants = String(item.Refrigerante || '')
          .split('/')
          .map((value) => value.trim().toUpperCase())
          .filter(Boolean);

        return itemRefrigerants.includes(String(refrigerante).trim().toUpperCase());
      });

      if (!filtered.length) {
        toastError('project', 'Nenhum compressor encontrado com a frequência e tensão selecionadas.');
        return;
      }

      const results: FindModelResult[] = [];
      const baseParams = { ...compressorParams };

      // eslint-disable-next-line no-restricted-syntax
      for (const item of filtered) {
        const displacementColumn = `Deslocamento ${parseInt(String(item.Frequencia || 0), 10)}Hz (m3h)`;
        const displacementValue = item[displacementColumn];

        if (displacementValue === undefined || displacementValue === null || displacementValue === '') {
          // eslint-disable-next-line no-continue
          continue;
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          const calcResult = await calculate(item, baseParams);

          if (calcResult) {
            results.push({
              compressorData: item,
              calculatedCapacity: calcResult.capacidade_refrigeracao,
              fullResults: calcResult,
            });
          }
        } catch (err) {
          console.error(`Erro ao calcular para ${String(item.Modelo || '-')}:`, err);
        }
      }

      if (!results.length) {
        toastError('project', 'Nenhum compressor compatível foi encontrado para os parâmetros informados.');
        return;
      }

      setFindModelsResults(results);
      setIsFindModelsOpen(true);
    } catch (err) {
      toastError('project', err instanceof Error ? err.message : 'Falha ao buscar modelos por capacidade.');
    } finally {
      setFindModelsLoading(false);
    }
  };

  const handleMatriz = async () => {
    if (!compressorData || !compressorData.Modelo) {
      toastError('project', 'Selecione um compressor antes de abrir a matriz de resultados.');
      return;
    }

    setIsMatrixOpen(true);
  };

  const handleGenerateMatrix = async (
    values,
    mode = 'standard'
  ) => {
    if (!compressorData || !compressorData.Modelo) {
      toastError('project', 'Selecione um compressor antes de gerar a matriz.');
      return;
    }

    setMatrixLoading(true);

    try {
      const evapMinUi = parseInputNumber(values.evapMin);
      const evapStepUi = parseInputNumber(values.evapStep);
      const evapMaxUi = parseInputNumber(values.evapMax);
      const condMinUi = parseInputNumber(values.condMin);
      const condStepUi = parseInputNumber(values.condStep);
      const condMaxUi = parseInputNumber(values.condMax);

      if (evapStepUi <= 0 || condStepUi <= 0) {
        throw new Error('Os passos da matriz devem ser maiores que zero.');
      }

      const evapMinC = tempUnit === 'f' ? fahrenheitToCelsius(evapMinUi) : evapMinUi;
      const evapStepC = tempUnit === 'f' ? deltaFToDeltaC(evapStepUi) : evapStepUi;
      const evapMaxC = tempUnit === 'f' ? fahrenheitToCelsius(evapMaxUi) : evapMaxUi;
      const condMinC = tempUnit === 'f' ? fahrenheitToCelsius(condMinUi) : condMinUi;
      const condStepC = tempUnit === 'f' ? deltaFToDeltaC(condStepUi) : condStepUi;
      const condMaxC = tempUnit === 'f' ? fahrenheitToCelsius(condMaxUi) : condMaxUi;
      const subcoolingK =
        tempUnit === 'f'
          ? deltaFToDeltaC(parseInputNumber(values.subcooling))
          : parseInputNumber(values.subcooling);
      const superheatK =
        tempUnit === 'f'
          ? deltaFToDeltaC(parseInputNumber(values.superheat))
          : parseInputNumber(values.superheat);

      const baseParams = {
        ...compressorParams,
        subResfriamento: String(subcoolingK),
        superaquecimentoTotal: String(superheatK),
      };

      const points = [];

      for (let evapC = evapMinC; evapC <= evapMaxC + evapStepC * 0.1; evapC += evapStepC) {
        for (let condC = condMinC; condC <= condMaxC + condStepC * 0.1; condC += condStepC) {
          if (isPointInsideEnvelope(refrigerante, evapC, condC, compressorData['Aplicação'])) {
            points.push({ evapC, condC });
          }
        }
      }

      const calculatedRows = await Promise.all(
        points.map(async (point) => {
          const params = {
            ...baseParams,
            temperaturaEvaporacao: String(point.evapC),
            temperaturaCondensacao: String(point.condC),
          };

          const result =
            mode === 'en12900'
              ? await calculateStandard(compressorData, params)
              : await calculate(compressorData, params);

          if (!result) {
            return null;
          }

          const standardResult = result;

          let capacidadeValue = 0;
          let potenciaValue = 0;
          let copValue = 0;
          let calorRejeitadoValue = 0;
          let correnteValue = 0;
          let temperaturaDescargaValue = 0;

          if (mode === 'en12900') {
            if ('capacidadeNormativa' in result) {
              capacidadeValue = result.capacidadeNormativa;
            }

            if ('potenciaConsumidaNormativa' in result) {
              potenciaValue = result.potenciaConsumidaNormativa;
            }

            if ('copNormativo' in result) {
              copValue = result.copNormativo;
            }

            if ('calorRejeitadoNormativo' in result) {
              calorRejeitadoValue = result.calorRejeitadoNormativo;
            }

            if ('correnteNormativa' in result) {
              correnteValue = result.correnteNormativa;
            }

            if ('tempDescargaRealNormativa' in result) {
              temperaturaDescargaValue = result.tempDescargaRealNormativa;
            }
          } else {
            if ('capacidade_refrigeracao' in result) {
              capacidadeValue = result.capacidade_refrigeracao;
            }

            if ('potencia_consumida' in result) {
              potenciaValue = result.potencia_consumida;
            }

            if ('cop' in result) {
              copValue = result.cop;
            }

            if ('calor_rejeitado' in result) {
              calorRejeitadoValue = result.calor_rejeitado;
            }

            if ('corrente' in result) {
              correnteValue = result.corrente;
            }

            if ('temp_descarga_real' in result) {
              temperaturaDescargaValue = result.temp_descarga_real;
            }
          }

          return {
            tempEvaporacao: displayTemperature(point.evapC),
            tempCondensacao: displayTemperature(point.condC),
            capacidadeRefrigeracao: convertCapacity(capacidadeValue),
            potenciaConsumida: potenciaValue,
            cop: copValue,
            calorRejeitado: convertCapacity(calorRejeitadoValue),
            corrente: correnteValue,
            temperaturaDescarga: displayTemperature(temperaturaDescargaValue),
          };
        })
      );

      const nextRows = calculatedRows.filter((row) => row !== null && row !== undefined);

      setMatrixRows(nextRows);

      if (nextRows.length > 0) {
        toastSuccess('project', `${nextRows.length} linhas geradas na matriz.`);
      } else {
        toastError('project', 'Nenhum ponto válido foi encontrado dentro do envelope.');
      }
    } catch (err) {
      toastError('project', err instanceof Error ? err.message : 'Falha ao gerar a matriz.');
      console.error(err);
    } finally {
      setMatrixLoading(false);
    }
  };

  const handleExportExcel = async () => {
    await exportCompressoresExcel({
      fileName: 'compressores.xls',
      inputRows: buildInputRows(),
      resultRows: buildResultRows(),
    });
  };

  const handleGeneratePDF = async () => {
    try {
      if (!pageRef.current) {
        throw new Error('Não foi possível localizar a página para exportação.');
      }

      await exportCompressoresPdf({
        element: pageRef.current,
        title: `Compressores - ${modelo || 'Relatório'}`,
      });

      toastSuccess('project', 'PDF gerado com sucesso!');
    } catch (err) {
      toastError('project', 'Falha ao gerar o arquivo');
      console.error(err);
    }
  };

  const topActions = [
    { label: t('Matriz de Resultados'), onClick: () => handleMatriz() },
    { label: t('Gerar Excel'), onClick: () => handleExportExcel() },
    { label: t('Gerar PDF'), onClick: () => handleGeneratePDF() },
  ];

  useEffect(() => {
    getCompressors(null);

    loadCoolProp()
      .then(() => {
        console.log('CoolProp carregado');
      })
      .catch((err) => {
        console.error(err);
        toastError('project', 'Erro ao carregar CoolProp');
      });
  }, []);

  useEffect(() => {
    const foundItem = compressors.find((item) => {
      const matchesModelo = !modelo || item.Modelo === modelo;
      const matchesFrequencia = !frequencia || String(item.Frequencia) === String(frequencia);
      const matchesTensao = !tensao || String(item['Tensão (V)']) === String(tensao);
      const matchesRefrigerante =
        !refrigerante ||
        String(item.Refrigerante || '')
          .split('/')
          .map((value) => value.trim())
          .includes(refrigerante);

      return matchesModelo && matchesFrequencia && matchesTensao && matchesRefrigerante;
    });

    setCompressorData(foundItem || {});
  }, [compressors, modelo, frequencia, tensao, refrigerante]);

  const handleCapacityUnitChange = (value: string) => {
    setCompressorParams((prev) => ({
      ...prev,
      unit: value,
      capacidade:
        prev.capacidade && prev.capacidade !== ''
          ? convertDisplayedCapacityValue(prev.capacidade, prev.unit || 'kw', value)
          : prev.capacidade,
    }));
  };

  const handleTempUnitChange = (value: string) => {
    const nextTempUnit: TempUnit = value === 'f' ? 'f' : 'c';

    setCompressorParams((prev) => ({
      ...prev,
      tempUnit: nextTempUnit,
      temperaturaCondensacao:
        prev.temperaturaCondensacao && prev.temperaturaCondensacao !== ''
          ? convertDisplayedTemperatureValue(
              prev.temperaturaCondensacao,
              prev.tempUnit === 'f' ? 'f' : 'c',
              nextTempUnit
            )
          : prev.temperaturaCondensacao,
      temperaturaEvaporacao:
        prev.temperaturaEvaporacao && prev.temperaturaEvaporacao !== ''
          ? convertDisplayedTemperatureValue(
              prev.temperaturaEvaporacao,
              prev.tempUnit === 'f' ? 'f' : 'c',
              nextTempUnit
            )
          : prev.temperaturaEvaporacao,
    }));
  };

  function setCapacidade(value: string): void {
    updateCompressorParam('capacidade', value);
  }

  return (
    <CompressoresContainer ref={pageRef}>
      {isGuestAccess && <img className="cs__guest-logo" src={logo} alt={t('Logo Elgin')} />}
      <Box align="center" mb={6}>
        <Text className="cs__page-title" style={{ fontSize: '50px' }}>
          <span>Seletor de </span>
          <span style={{ fontWeight: 'bold' }}>Compressores</span>
        </Text>
        <Text className="cs__page-title" style={{ fontSize: '26px' }}>
          {t(isHermetico ? 'Herméticos' : 'Semi-Herméticos')}
        </Text>
      </Box>
      <Box className="cs__page">
        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={4}>
          <VStack spacing={4} align="stretch">
            <p className="chakra-text cs__card-title css-0">{t('Dados de Entrada')}</p>
            <Card>
              <Box>
                <RadioGroup value={searchMode} onChange={handleSearchModeChange}>
                  <HStack spacing={8} mt={2}>
                    <Radio value="modelo" colorScheme="blue">
                      <Text fontWeight={searchMode === 'modelo' ? 'bold' : 'normal'} style={{ fontSize: '18px' }}>
                        {t('Por Modelo')}
                      </Text>
                    </Radio>
                    <Radio value="capacidade" colorScheme="blue">
                      <Text fontWeight={searchMode === 'capacidade' ? 'bold' : 'normal'} style={{ fontSize: '18px' }}>
                        {t('Por Capacidade')}
                      </Text>
                    </Radio>
                  </HStack>
                </RadioGroup>
              </Box>

              <VStack spacing={4} align="stretch" mt={5}>
                {searchMode === 'modelo' && (
                  <Field label={t('Modelo')}>
                    <CsSelect
                      value={compressorParams.modelo}
                      options={[
                        { value: '', label:  t('Selecione') },
                        ...modeloOptions
                      ]}
                      isSearchable
                      searchPlaceholder={t('Pesquisar modelo...')}
                      onChange={(value) => updateCompressorParam('modelo', value)}
                    />
                  </Field>
                )}

                {searchMode === 'capacidade' && (
                  <Field label={`${t('Capacidade Necessária')} (${capacitySuffix})`}>
                    {/* eslint-disable-next-line react/jsx-no-bind */}
                    <CsInput value={capacidade} onChange={setCapacidade} placeholder="0" maxLength={6} />
                  </Field>
                )}

                <Field label={t('Frequência (Hz)')}>
                  <CsSelect
                    value={frequencia}
                    options={[
                      { value: '', label: t('Selecione')},
                      ...frequenciaOptions
                    ]}
                    onChange={(value) => updateCompressorParam('frequencia', value)}
                  />
                </Field>

                <Field label={t('Tensão (V)')}>
                  <CsSelect
                    value={tensao}
                    options={[
                      { value: '', label:  t('Selecione') },
                      ...tensaoOptions
                    ]}
                    onChange={(value) => updateCompressorParam('tensao', value)}
                  />
                </Field>                

                <Field label={t('Refrigerante')}>
                  <CsSelect
                    value={refrigerante}
                    options={[
                      { value: '', label:  t('Selecione') },
                      ...refrigeranteOptions
                    ]}
                    onChange={(value) => updateCompressorParam('refrigerante', value)}
                  />
                </Field>

                <Field label={t('Temperatura de Condensação')} suffix={temperatureSuffix}>
                  <CsInput
                    value={temperaturaCondensacao}
                    onChange={(value) => updateCompressorParam('temperaturaCondensacao', value)}
                    placeholder="0"
                    maxLength={6}
                  />
                </Field>

                <Field label={t('Temperatura de Evaporação')} suffix={temperatureSuffix}>
                  <CsInput
                    value={temperaturaEvaporacao}
                    onChange={(value) => updateCompressorParam('temperaturaEvaporacao', value)}
                    placeholder="0"
                    maxLength={6}
                  />
                </Field>

                <Field label={t('Superaquecimento Total')} suffix="K">
                  <CsInput
                    value={superaquecimentoTotal}
                    onChange={(value) => updateCompressorParam('superaquecimentoTotal', value)}
                    placeholder="0"
                    maxLength={6}
                  />
                </Field>

                <Field
                  suffix="K"
                  label={
                    <HStack spacing={2} align="center">
                      <Checkbox
                        isChecked={useSuperaquecimentoUtil}
                        onChange={(e) => updateCompressorParam('useSuperaquecimentoUtil', e.target.checked)}
                        colorScheme="blue"
                      />
                      <Text
                        as="span"
                        className="cs__label"
                        color={useSuperaquecimentoUtil ? undefined : 'gray.50'}
                        opacity={useSuperaquecimentoUtil ? 1 : 0.3}
                      >
                        {t('Superaquecimento Útil')}
                      </Text>
                    </HStack>
                  }
                >
                  <CsInput
                    value={superaquecimentoUtil}
                    onChange={(value) => updateCompressorParam('superaquecimentoUtil', value)}
                    placeholder="0"
                    maxLength={6}
                    isDisabled={!useSuperaquecimentoUtil}
                  />
                </Field>

                <Field label="Sub-resfriamento" suffix="K">
                  <CsInput value={subResfriamento} onChange={(value) => updateCompressorParam('subResfriamento', value)} placeholder="0" maxLength={6} />
                </Field>


                {searchMode === 'capacidade' && (
                  <Button
                    $primary
                    style={{ width: '100%', fontSize: '18px', background: colors.primary, color: colors.white }}
                    onClick={() => handleFindModels()}
                    isDisabled={findModelsLoading}
                  >
                    {findModelsLoading ? t('Buscando...') : t('Buscar Modelos')}
                  </Button>
                )}

                {searchMode !== 'capacidade' && (
                <HStack justify="flex-center" className="cs__actions" pt={1}>
                  <Button
                    $primary
                    style={{ width: '100%', fontSize: '18px', background: colors.primary, color: colors.white }}
                    onClick={() => handleCalculate(compressorData, compressorParams)}
                  >
                    {t('Calcular')}
                  </Button>
                </HStack>
                )}


              </VStack>
            </Card>
            <HStack spacing={3} align="flex-end" w="100%" flexWrap="nowrap">
              <Box flex="1" minW="0">
                <Field prefix={t('Capacidade')}>
                  <CsSelect
                    value={compressorParams.unit}
                    pillBg="blue.500"
                    textColor="white"
                    options={capacidadeOptions}
                    onChange={handleCapacityUnitChange}
                  />
                </Field>
              </Box>
              <Box flex="1" minW="0">
                <Field prefix={t('Temperatura')}>
                  <CsSelect
                    value={compressorParams.tempUnit}
                    pillBg="blue.500"
                    textColor="white"
                    options={temperaturaOptions}
                    onChange={handleTempUnitChange}
                  />
                </Field>
              </Box>
            </HStack>
      </VStack>

          {/* COL 2 - Resultados */}
          <VStack spacing={4} align="stretch">
            <p className="chakra-text cs__card-title css-0"> {t('Resultados')}</p>
            <Card>
              <Tabs
                variant="unstyled"
                onChange={(idx) => setTab(idx === 0 ? 'geral' : 'dados-fisicos')}
                defaultIndex={0}
              >
                <TabList className="cs__tabs">
                  <Tab className={`cs__tab ${tab === 'geral' ? 'is-active' : ''}`}>{t('Geral')}</Tab>
                  <Tab className={`cs__tab ${tab === 'dados-fisicos' ? 'is-active' : ''}`}>{t('Dados Físicos')}</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel px={0} pt={4}>
                    <VStack spacing={4} align="stretch">
                      <Box className="cs__block">
                        <Text className="cs__block-title">{t('Performance')}</Text>
                        <VStack spacing={2} align="stretch" mt={2}>
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Capacidade de Refrigeração')} ({capacitySuffix})</Text>
                            <Text className="cs__kv-v">{convertCapacity(compressorResult.capacidadeRefrigeracao)?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">
                              {t('Capacidade de Refrigeração')} {calculationModel.standardDisplayName(compressorData?.['Aplicação'])} ({capacitySuffix})
                            </Text>
                            <Text className="cs__kv-v">{convertCapacity(compressorResult.capacidadeRefrigeracaoNormativa)?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Calor Rejeitado')} ({capacitySuffix})</Text>
                            <Text className="cs__kv-v">{convertCapacity(compressorResult.calorRejeitado)?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('COP')}</Text>
                            <Text className="cs__kv-v">{compressorResult.cop?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">
                              {t('COP')} {calculationModel.standardDisplayName(compressorData?.['Aplicação'])}
                            </Text>
                            <Text className="cs__kv-v">{compressorResult.copNormativo?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                        </VStack>
                      </Box>

                      <Box className="cs__block">
                        <Text className="cs__block-title">{t('Dados Gerais')}</Text>
                        <VStack spacing={2} align="stretch" mt={2}>
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Potência Consumida')} (kW)</Text>
                            <Text className="cs__kv-v">{compressorResult.potenciaConsumida?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Corrente (A)')}</Text>
                            <Text className="cs__kv-v">{compressorResult.corrente?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Refrigerante')}</Text>
                            <Text className="cs__kv-v">{compressorResult.refrigerante}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Vazão Mássica')} (kg/h)</Text>
                            <Text className="cs__kv-v">{compressorResult.vazaoMassica?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Deslocamento Nominal')} (m³/h)</Text>
                            <Text className="cs__kv-v">{compressorResult.deslocamentoNominal?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                        </VStack>
                      </Box>

                      <Box className="cs__block">
                        <Text className="cs__block-title">{t('Dados Ciclo')}</Text>
                        <VStack spacing={2} align="stretch" mt={2}>
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Pressão de Descarga')} (bar)</Text>
                            <Text className="cs__kv-v">{compressorResult.pressaoDescarga?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Pressão de Sucção')} (bar)</Text>
                            <Text className="cs__kv-v">{compressorResult.pressaoSuccao?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Temperatura de Descarga')} ({temperatureSuffix})</Text>
                            <Text className="cs__kv-v">{displayTemperature(compressorResult.temperaturaDescarga)?.toFixed(2)}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                        </VStack>
                      </Box>
                    </VStack>
                  </TabPanel>

                  <TabPanel px={0} pt={4}>
                    <Box className="cs__image-box">
                      {compressorImage ? (
                        <Image
                          className="cs__image"
                          src={compressorImage}
                          alt="Compressor"
                          onError={() => {
                            if (compressorImageBase && compressorImageExtension === 'png') {
                              setCompressorImageExtension('jpg');
                            }
                          }}
                        />
                      ) : null}
                    </Box>
                    <VStack spacing={4} align="stretch">
                      <Box className="cs__block">
                        <Text className="cs__block-title">{t('Dados do Compressor')}</Text>
                        <VStack spacing={2} align="stretch" mt={2}>
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Marca')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["Marca"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Tipo')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["Tipo"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{isHermetico ? t('Modelo') : 'physical_model (V)'}</Text>
                            <Text className="cs__kv-v">{compressorData?.["Modelo"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('HP')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["HP"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Aplicação')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["Aplicação"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          {!isHermetico ? (
                            <>
                              <HStack justify="space-between">
                                <Text className="cs__kv-k">{t('Cilindros')}</Text>
                                <Text className="cs__kv-v">{compressorData?.['Cilindros'] ?? ''}</Text>
                              </HStack>
                              <Divider borderColor="blue.400" />
                            </>
                          ) : null}
                        </VStack>
                      </Box>

                      <Box className="cs__block">
                        <Text className="cs__block-title">{t('Dados Técnicos')}</Text>
                        <VStack spacing={2} align="stretch" mt={2}>
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Peso (kg)')}</Text>
                            <Text className="cs__kv-v">{formatDecimalValue(compressorData?.['Peso'])}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Sucção (in)')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["Sucção (in)"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Descarga (in)')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["Descarga (in)"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Óleo')}</Text>
                            <Text className="cs__kv-v">{compressorData?.[isHermetico ? 'Óleo' : 'Oleo'] ?? ''}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('Carga de Óleo (L)')}</Text>
                            <Text className="cs__kv-v">
                              {compressorData?.[isHermetico ? 'Carga de Óleo (L)' : 'Carga de oleo'] ?? ''}
                            </Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          {!isHermetico ? (
                            <>
                              <HStack justify="space-between">
                                <Text className="cs__kv-k">{t('Pressão Máxima LP (bar)')}</Text>
                                <Text className="cs__kv-v">{compressorData?.['Pressão Maxima (LP)'] ?? ''}</Text>
                              </HStack>
                              <Divider borderColor="blue.400" />
                              <HStack justify="space-between">
                                <Text className="cs__kv-k">{t('Pressão Máxima HP (bar)')}</Text>
                                <Text className="cs__kv-v">{compressorData?.['Pressão Maxima (HP)'] ?? ''}</Text>
                              </HStack>
                              <Divider borderColor="blue.400" />
                            </>
                          ) : null}
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t(isHermetico ? 'LRA (A)' : 'RLA (A)')}</Text>
                            <Text className="cs__kv-v">
                              {compressorData?.[isHermetico ? 'LRA (A)' : 'RLA (A)'] ?? ''}
                            </Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('MCC (A)')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["MCC (A)"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t(isHermetico ? 'Capacitor de Partida' : 'IP')}</Text>
                            <Text className="cs__kv-v">
                              {compressorData?.[isHermetico ? 'Capacitor de Partida' : 'IP'] ?? ''}
                            </Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">
                              {t(isHermetico ? 'Capacitor de Trabalho' : 'Fechamento Elétrico')}
                            </Text>
                            <Text className="cs__kv-v">
                              {compressorData?.[isHermetico ? 'Capacitor de Trabalho' : 'Fechamento elétrico'] ?? ''}
                            </Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          {isHermetico ? (
                            <>
                              <HStack justify="space-between">
                                <Text className="cs__kv-k">{t('Relé')}</Text>
                                <Text className="cs__kv-v">{compressorData?.['Relé'] ?? ''}</Text>
                              </HStack>
                              <Divider borderColor="blue.400" />
                            </>
                          ) : null}
                        </VStack>
                      </Box>

                      {!isHermetico ? <Box className="cs__block">
                        <Text className="cs__block-title">{t('Dimensões')}</Text>
                        <VStack spacing={2} align="stretch" mt={2}>
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('A')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["A (mm)"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('B')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["B (mm)"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('C')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["C (mm)"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                          <HStack justify="space-between">
                            <Text className="cs__kv-k">{t('D')}</Text>
                            <Text className="cs__kv-v">{compressorData?.["D (mm)"] ?? ""}</Text>
                          </HStack>
                          <Divider borderColor="blue.400" />
                        </VStack>
                      </Box> : null}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Card>
          </VStack>

          {/* COL 3 - Gráficos */}
          <VStack spacing={4} align="stretch">
            <HStack flexWrap="wrap" className="cs__actions">
              {topActions.map((a) => (
                <Button $primary style={{ fontSize: '18px' }} key={a.label} onClick={a.onClick}>
                  {a.label}
                </Button>
              ))}
            </HStack>
            <Card>
              <VStack spacing={5} align="stretch">
                <Box className="cs__graph-box" minH={{ base: "420px", xl: "560px" }}   height={{ base: "550px", xl: "610px" }}
  px={{ base: 1, xl: 2 }} py={1}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PHDiagramChart
                      data={phDiagramData}
                      isLoading={phDiagramLoading}
                      getTextFunc={(key) => {
                        const texts: Record<string, string> = {
                          enthalpy: t('Entalpia'),
                          pressure: t('Pressão'),
                          saturation_curve: t('Curva de saturação'),
                          refrigeration_cycle: t('Ciclo de refrigeração'),
                          no_data: t('Execute o cálculo para visualizar o diagrama P-h.'),
                          loading: t('Gerando diagrama P-h...')
                        };

                        return texts[key] || key;
                      }}
                    />
                  </ResponsiveContainer>
                </Box>

                <Divider borderColor="blue.400" />

                <Box className="cs__graph-box" minH={{ base: "340px", xl: "460px" }} px={{ base: 1, xl: 2 }} py={1}>
                  <ResponsiveContainer width="100%" height="100%">
                    <OperationEnvelopeChart
                      refrigerante={refrigerante}
                      evapTemp={temperaturaEvaporacao ? normalizeTemperatureToCelsius(temperaturaEvaporacao) : undefined}
                      condTemp={temperaturaCondensacao ? normalizeTemperatureToCelsius(temperaturaCondensacao) : undefined}
                      tempUnit={tempUnit === 'f' ? 'f' : 'c'}
                      family={calculationModel.family}
                      application={compressorData?.['Aplicação']}
                    />
                  </ResponsiveContainer>
                </Box>
              </VStack>
            </Card>
          </VStack>
        </SimpleGrid>
      </Box>

      <FindModelsModal
        isHermetico={isHermetico}
        isOpen={isFindModelsOpen}
        onClose={() => setIsFindModelsOpen(false)}
        requiredCapacityKw={findModelsRequiredCapacity}
        requiredCapacityDisplay={convertCapacity(findModelsRequiredCapacity)}
        capacitySuffix={capacitySuffix}
        results={findModelsResults}
        applicationFilter={findModelsApplicationFilter}
        onApplicationFilterChange={setFindModelsApplicationFilter}
        onSelectModel={handleSelectFoundModel}
        convertCapacityValue={convertCapacity}
      />

      <MatrixWindow
        isOpen={isMatrixOpen}
        onClose={() => setIsMatrixOpen(false)}
        tempUnit={tempUnit === 'f' ? 'f' : 'c'}
        capacitySuffix={capacitySuffix}
        normativeLabel={calculationModel.standardDisplayName(compressorData?.['Aplicação'])}
        initialValues={buildMatrixInitialValues()}
        rows={matrixRows}
        isLoading={matrixLoading}
        onGenerate={handleGenerateMatrix}
      />
    </CompressoresContainer>
  );
};

export default Compressores;
